import dynamic from "next/dynamic";
import { useRouter } from "next/router";

import React, { useCallback, useMemo } from "react";

import { addYears } from "date-fns";
import format from "date-fns/format";
import isArray from "lodash/isArray";
import { Entity } from "models/entity";
import numeral from "numeral";
import { twMerge } from "tailwind-merge";

import { SKSpinner } from "components/customElements/skSpinner";
import EmptyState from "components/EmptyState";
import { ToggleButtons } from "components/EntityPageLayout/EntityPageData/ToggleTimeScope";
import tableStyle from "components/Table/style.module.scss";
import { useApi } from "hooks/useApi";
import { baseFetcher } from "utils/baseFetcher";

import { estimatesUrl, isDataEmpty } from "../utils";

import { AVAILABLE_KEYS, DEFAULT_KEY } from "./utils";

const ComparisonChartSection = dynamic(
  () => import("./ComparisonChartSection"),
  {
    ssr: false,
  }
);

interface EstimatesProps {
  entity: Entity;
  showEarningMatrix?: boolean;
}

const Estimates = ({ entity, showEarningMatrix }: EstimatesProps) => {
  const router = useRouter();
  const key = (router.query.key as string) || DEFAULT_KEY;

  const setKey = useCallback((key) => {
    router.replace(
      {
        pathname: router.pathname,
        query: {
          ...router.query,
          key: key.id,
        },
      },
      undefined,
      {
        shallow: true,
        scroll: false,
      }
    );
  }, []);

  const { data: estimatesData, isValidating } = useApi(
    estimatesUrl(entity.id),
    baseFetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    }
  );

  const {
    ["fest_actsales-annual"]: festActsalesAnnual,
    ["fest_actebit-annual"]: festActebitAnnual,
    ["fest_actebitda-annual"]: festActebitdaAnnual,
    ["fest_acteps_rpt-annual"]: festActepsRptAnnual,
    ["fest_acteps-annual"]: festActepsAnnual,
    fest_esteps_gap_mean_rp_fq0,
    fest_esteps_gap_mean_rp_fq1,
    fest_esteps_gap_mean_rp_fq2,
    fest_esteps_gap_mean_rp_fq3,
    fest_esteps_gap_mean_rp_fq4,
    fest_esteps_gap_mean_rp_fq5,
    fest_esteps_gap_mean_rp_fq6,
    fest_esteps_gap_mean_rp_fq7,
    fest_esteps_gap_mean_rp_fq8,
    fest_esteps_gap_mean_rp_fq9,
    fest_esteps_gap_mean_rp_fq10,
    fest_esteps_gap_mean_rp_fq11,

    fest_estsales_rp_fy0,
    fest_estebitda_rp_fy0,
    fest_estebit_rp_fy0,
    fest_esteps_gap_mean_rp_fy0,
    fest_esteps_rp_fy0,

    fest_estsales_rp_fy1,
    fest_estebitda_rp_fy1,
    fest_estebit_rp_fy1,
    fest_esteps_gap_mean_rp_fy1,
    fest_esteps_rp_fy1,

    fest_estsales_rp_fy2,
    fest_estebitda_rp_fy2,
    fest_estebit_rp_fy2,
    fest_esteps_gap_mean_rp_fy2,
    fest_esteps_rp_fy2,

    fest_quarter_acteps_rpt,

    fest_actsales_prev,
    fest_actebitda_prev,
    fest_actebit_prev,
    fest_acteps_prev,
    fest_acteps_rpt_prev,
  } = estimatesData || {};

  const estimatesQuarterData: any = useMemo(() => {
    const lastYear = addYears(new Date(), -1).getFullYear();

    if (key === "epsgaap") {
      return [
        fest_esteps_gap_mean_rp_fq11,
        fest_esteps_gap_mean_rp_fq10,
        fest_esteps_gap_mean_rp_fq9,
        fest_esteps_gap_mean_rp_fq8,
        fest_esteps_gap_mean_rp_fq7,
        fest_esteps_gap_mean_rp_fq6,
        fest_esteps_gap_mean_rp_fq5,
        fest_esteps_gap_mean_rp_fq4,
        fest_esteps_gap_mean_rp_fq3,
        fest_esteps_gap_mean_rp_fq2,
        fest_esteps_gap_mean_rp_fq1,
        fest_esteps_gap_mean_rp_fq0,
        ...(isArray(fest_quarter_acteps_rpt) ? fest_quarter_acteps_rpt : []),
      ]
        .filter(
          (i) =>
            !!i &&
            !i.code &&
            parseInt(i.label?.match(/\dQ\s(\d{4})/)[1]) >= lastYear
        )
        .slice(0, 16)
        .reverse();
    }

    return [
      estimatesData?.[`fest_est${key}_rp_fq11`],
      estimatesData?.[`fest_est${key}_rp_fq10`],
      estimatesData?.[`fest_est${key}_rp_fq9`],
      estimatesData?.[`fest_est${key}_rp_fq8`],
      estimatesData?.[`fest_est${key}_rp_fq7`],
      estimatesData?.[`fest_est${key}_rp_fq6`],
      estimatesData?.[`fest_est${key}_rp_fq5`],
      estimatesData?.[`fest_est${key}_rp_fq4`],
      estimatesData?.[`fest_est${key}_rp_fq3`],
      estimatesData?.[`fest_est${key}_rp_fq2`],
      estimatesData?.[`fest_est${key}_rp_fq1`],
      estimatesData?.[`fest_est${key}_rp_fq0`],
      ...(isArray(estimatesData?.[`fest_quarter_act${key}`])
        ? estimatesData?.[`fest_quarter_act${key}`]
        : []),
    ]
      .filter(
        (i) =>
          !!i &&
          !i.code &&
          parseInt(i.label?.match(/\dQ\s(\d{4})/)[1]) >= lastYear
      )
      .slice(0, 16)
      .reverse();
  }, [estimatesData, key]);

  const estimatesTableData: any = useMemo(() => {
    const quarters = [
      [
        estimatesQuarterData[0],
        estimatesQuarterData[4],
        estimatesQuarterData[8],
        estimatesQuarterData[12],
      ],
      [
        estimatesQuarterData[1],
        estimatesQuarterData[5],
        estimatesQuarterData[9],
        estimatesQuarterData[13],
      ],
      [
        estimatesQuarterData[2],
        estimatesQuarterData[6],
        estimatesQuarterData[10],
        estimatesQuarterData[14],
      ],
      [
        estimatesQuarterData[3],
        estimatesQuarterData[5],
        estimatesQuarterData[11],
        estimatesQuarterData[15],
      ],
    ];

    if (key === "sales") {
      return [
        ...quarters,
        [
          festActsalesAnnual,
          fest_estsales_rp_fy0,
          fest_estsales_rp_fy1,
          fest_estsales_rp_fy2,
        ],
        [
          festActsalesAnnual?.value / fest_actsales_prev?.value - 1,
          fest_estsales_rp_fy0?.value / festActsalesAnnual?.value - 1,
          fest_estsales_rp_fy1?.value / fest_estsales_rp_fy0?.value - 1,
          fest_estsales_rp_fy2?.value / fest_estsales_rp_fy1?.value - 1,
        ],
      ];
    }

    if (key === "ebitda") {
      return [
        ...quarters,
        [
          festActebitdaAnnual,
          fest_estebitda_rp_fy0,
          fest_estebitda_rp_fy1,
          fest_estebitda_rp_fy2,
        ],
        [
          festActebitdaAnnual?.value / fest_actebitda_prev?.value - 1,
          fest_estebitda_rp_fy0?.value / festActebitdaAnnual?.value - 1,
          fest_estebitda_rp_fy1?.value / fest_estebitda_rp_fy0?.value - 1,
          fest_estebitda_rp_fy2?.value / fest_estebitda_rp_fy1?.value - 1,
        ],
      ];
    }

    if (key === "ebit") {
      return [
        ...quarters,
        [
          festActebitAnnual,
          fest_estebit_rp_fy0,
          fest_estebit_rp_fy1,
          fest_estebit_rp_fy2,
        ],
        [
          festActebitAnnual?.value / fest_actebit_prev?.value - 1,
          fest_estebit_rp_fy0?.value / festActebitAnnual?.value - 1,
          fest_estebit_rp_fy1?.value / fest_estebit_rp_fy0?.value - 1,
          fest_estebit_rp_fy2?.value / fest_estebit_rp_fy1?.value - 1,
        ],
      ];
    }

    if (key === "eps") {
      return [
        ...quarters,
        [
          festActepsAnnual,
          fest_esteps_rp_fy0,
          fest_esteps_rp_fy1,
          fest_esteps_rp_fy2,
        ],
        [
          festActepsAnnual?.value / fest_acteps_prev?.value - 1,
          fest_esteps_rp_fy0?.value / festActepsAnnual?.value - 1,
          fest_esteps_rp_fy1?.value / fest_esteps_rp_fy0?.value - 1,
          fest_esteps_rp_fy2?.value / fest_esteps_rp_fy1?.value - 1,
        ],
      ];
    }

    if (key === "epsgaap") {
      return [
        ...quarters,
        [
          festActepsRptAnnual,
          fest_esteps_gap_mean_rp_fy0,
          fest_esteps_gap_mean_rp_fy1,
          fest_esteps_gap_mean_rp_fy2,
        ],
        [
          festActepsRptAnnual?.value / fest_acteps_rpt_prev?.value - 1,
          fest_esteps_gap_mean_rp_fy0?.value / festActepsRptAnnual?.value - 1,
          fest_esteps_gap_mean_rp_fy1?.value /
            fest_esteps_gap_mean_rp_fy0?.value -
            1,
          fest_esteps_gap_mean_rp_fy2?.value /
            fest_esteps_gap_mean_rp_fy1?.value -
            1,
        ],
      ];
    }
  }, [estimatesData, key]);

  const estimatesTableValueMultiplier = useMemo(() => {
    if (["sales", "ebitda", "ebit"].includes(key)) return 1000000;
    if (["eps", "epsgaap"].includes(key)) return 1;
    return 1;
  }, [key]);

  const yearlyEstimatesTableData = estimatesTableData?.[4];

  const estimatesTableValueCurrency = useMemo(() => {
    if (["sales", "ebitda", "ebit"].includes(key)) {
      return `${
        estimatesTableData?.[0]?.[0]?.currency ||
        yearlyEstimatesTableData?.[0]?.currency
      } `;
    }

    if (["eps", "epsgaap"].includes(key)) return "";
    return "";
  }, [key, estimatesTableData, yearlyEstimatesTableData]);

  if (!isValidating && isDataEmpty(estimatesData))
    return (
      <>
        <h5 className="text-[16px] text-center mb-[15px]">
          Reported & Estimated Earnings
        </h5>
        <EmptyState type="data" />
      </>
    );

  return (
    <div>
      <div>
        <div className="flex pb-[45px]">
          <ToggleButtons
            options={AVAILABLE_KEYS.map(({ id, title }) => ({
              id,
              text: title,
            }))}
            value={key}
            setValue={(id) => setKey(AVAILABLE_KEYS.find((k) => k.id === id))}
            buttonClassName={() => "px-[10px]"}
          />
        </div>
        <div
          className={twMerge(
            "pb-[10px]",
            "font-medium",
            "text-[16px] text-center",
            "text-[#1f1f20]"
          )}
        >
          Stock Price vs Consensus Estimates (Rolling NTM)
        </div>
        <ComparisonChartSection entity={entity} />
      </div>

      <div className="flex flex-col space-y-[30px]">
        {showEarningMatrix && (
          <div className="flex-1">
            <h5 className="text-[16px] text-center mb-[15px]">
              Reported & Estimated Earnings
            </h5>
            {isValidating ? (
              <SKSpinner />
            ) : (
              estimatesData && (
                <div className="overflow-x-auto">
                  <table
                    className={twMerge(tableStyle.table, "table-auto w-full")}
                  >
                    <tbody>
                      <tr className="h-[35px]">
                        <td className="w-[20%]"></td>
                        <td className="w-[20%] text-right">
                          {yearlyEstimatesTableData?.[0]?.label}
                        </td>
                        <td className="w-[20%] text-right">
                          {yearlyEstimatesTableData?.[1]?.label}
                        </td>
                        <td className="w-[20%] text-right">
                          {yearlyEstimatesTableData?.[2]?.label}
                        </td>
                        <td className="w-[20%] text-right">
                          {yearlyEstimatesTableData?.[3]?.label}
                        </td>
                      </tr>
                      <tr className="border-0 border-b border-gray-300">
                        <td className="font-bold">
                          1Q{" "}
                          {estimatesQuarterData?.[0]?.date ? (
                            <>
                              {format(
                                new Date(estimatesQuarterData?.[0]?.date),
                                "MMM"
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[0]?.[0]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[0]?.[0]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[0]?.[1]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[0]?.[1]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[0]?.[2]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[0]?.[2]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[0]?.[3]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[0]?.[3]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr className="border-0 border-b border-gray-300">
                        <td className="font-bold">
                          2Q{" "}
                          {estimatesQuarterData?.[1]?.date ? (
                            <>
                              {format(
                                new Date(estimatesQuarterData?.[1]?.date),
                                "MMM"
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[1]?.[0]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[1]?.[0]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[1]?.[1]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[1]?.[1]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[1]?.[2]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[1]?.[2]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[1]?.[3]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[1]?.[3]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr className="border-0 border-b border-gray-300">
                        <td className="font-bold">
                          3Q{" "}
                          {estimatesQuarterData?.[2]?.date ? (
                            <>
                              {format(
                                new Date(estimatesQuarterData?.[2]?.date),
                                "MMM"
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[2]?.[0]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[2]?.[0]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[2]?.[1]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[2]?.[1]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[2]?.[2]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[2]?.[2]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[2]?.[3]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[2]?.[3]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr className="border-0 border-b border-gray-300">
                        <td className="font-bold">
                          4Q{" "}
                          {estimatesQuarterData?.[3]?.date ? (
                            <>
                              {format(
                                new Date(estimatesQuarterData?.[3]?.date),
                                "MMM"
                              )}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[3]?.[0]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[3]?.[0]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[3]?.[1]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[3]?.[1]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[3]?.[2]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[3]?.[2]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof estimatesTableData?.[3]?.[3]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                estimatesTableData?.[3]?.[3]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr className="border-0 border-b border-gray-300">
                        <td className="font-bold">Year</td>
                        <td className="whitespace-nowrap text-right">
                          {typeof yearlyEstimatesTableData?.[0]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                yearlyEstimatesTableData?.[0]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof yearlyEstimatesTableData?.[1]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                yearlyEstimatesTableData?.[1]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof yearlyEstimatesTableData?.[2]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                yearlyEstimatesTableData?.[2]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="whitespace-nowrap text-right">
                          {typeof yearlyEstimatesTableData?.[3]?.value !=
                          "undefined" ? (
                            <>
                              {estimatesTableValueCurrency}
                              {numeral(
                                yearlyEstimatesTableData?.[3]?.value *
                                  estimatesTableValueMultiplier
                              ).format("0,0.[00]a")}
                            </>
                          ) : (
                            "-"
                          )}
                        </td>
                      </tr>
                      <tr className="">
                        <td className="font-bold">Growth</td>
                        <td
                          className={twMerge(
                            "text-right",
                            estimatesTableData?.[5]?.[0] < 0
                              ? "text-bearish-red"
                              : "text-bullish-green"
                          )}
                        >
                          {typeof estimatesTableData?.[5]?.[0] == "number"
                            ? numeral(estimatesTableData?.[5]?.[0]).format(
                                "0.00%"
                              )
                            : "-"}
                        </td>
                        <td
                          className={twMerge(
                            "text-right",
                            estimatesTableData?.[5]?.[1] < 0
                              ? "text-bearish-red"
                              : "text-bullish-green"
                          )}
                        >
                          {typeof estimatesTableData?.[5]?.[1] == "number"
                            ? numeral(estimatesTableData?.[5]?.[1]).format(
                                "0.00%"
                              )
                            : "-"}
                        </td>
                        <td
                          className={twMerge(
                            "text-right",
                            estimatesTableData?.[5]?.[2] < 0
                              ? "text-bearish-red"
                              : "text-bullish-green"
                          )}
                        >
                          {typeof estimatesTableData?.[5]?.[2] == "number"
                            ? numeral(estimatesTableData?.[5]?.[2]).format(
                                "0.00%"
                              )
                            : "-"}
                        </td>
                        <td
                          className={twMerge(
                            "text-right",
                            estimatesTableData?.[5]?.[3] < 0
                              ? "text-bearish-red"
                              : "text-bullish-green"
                          )}
                        >
                          {typeof estimatesTableData?.[5]?.[3] == "number"
                            ? numeral(estimatesTableData?.[5]?.[3]).format(
                                "0.00%"
                              )
                            : "-"}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Estimates;
